<template>
  <reservation-data-card
    :image="image"
    :room="room"
    :date="date"
    :time="time"
    :attendees="attendees"
    :title="title"
    :whiteOverlay="isCompleted"
    :displayRoomOrAttendees="true"
  >
    <template v-slot:image-overlay>
      <v-chip
        label
        small
        :color="activityReservationColorsByStatusDark[isUpcoming ? 'UPCOMING' : status].background"
        :text-color="activityReservationColorsByStatusDark[isUpcoming ? 'UPCOMING' : status].text"
        class="float-left float-top status-chip"
      >
        {{ statusText }}
      </v-chip>
      <div class="float-top float-right white--text H8">{{ reservationMethod }}</div>

      <div small color="white" v-if="totalPrice" class="price-chip float-right float-bottom" :class="{'two-currencies': showDisplayCurrency}">
        <span v-if="showDisplayCurrency" class="grey-2--text show-spaces pricing-display-number-text">{{ getPriceStringNoCurrency({ amount: totalPrice, currency: baseCurrency.currency, displayCurrency: displayCurrency }) }} {{ displayCurrency.currency.displayName }}</span>
        <span class="primary--text text--darken-1"><span class="pricing-number-text">{{ getPriceStringNoCurrency({ amount: totalPrice, currency: baseCurrency.currency }) }}</span><span class="pricing-currency-text"> {{ baseCurrency.currency.displayName }}</span></span>
      </div>
    </template>

    <template v-slot:buttons>
      <v-flex v-if="ticketFileId">
        <v-btn @click="openTicket()" color="primary lighten-1" block outlined>{{$t('reservations.ticket')}}</v-btn>
      </v-flex>
      <v-flex>
        <v-btn @click="goTo()" color="primary lighten-1" block>{{$t('reservations.see_details')}}</v-btn>
      </v-flex>
    </template>
  </reservation-data-card>
</template>
  
<script>
import { mapGetters } from 'vuex'
import { displayFormatDate } from '@/lib/helpers/DateUtils'
import { getTimeFormat } from '@/lib/helpers/HoursFormatter'
import { getTextValue } from '@/lib/helpers/LocalizedText'
import { reservationTypes, activityReservationColorsByStatusDark, activityReservationStatus, orderLineTypes } from '@/config'
import moment from 'moment'
import { ActivityReservationsRequests } from '@/services/activityReservations.requests'

const activityReservationsService = new ActivityReservationsRequests()
  
export default {
  props: {
    reservation: Object,
  },
  data () {
    return {
      activityReservationColorsByStatusDark,
    }
  },
  components: {
    ReservationDataCard: () => import('@/components/view-components/reservations/ReservationDataCard.vue'),
  },
  computed: {
    ...mapGetters('me', ['getPriceStringNoCurrency', 'showDisplayCurrency', 'displayCurrency', 'baseCurrency']),
    order () {
      return this.reservation.order || this.reservation.pendingOrder
    },
    subtotal () {
      if (!this.order) return null
      return this.order.orderLines.reduce((acc, orderLine) => {
        if (orderLine.parentOrderLine) {
          if (!orderLine.includedOnParentLine && orderLine.orderLineType !== orderLineTypes.SERVICE_CHARGE) {
            return acc + (orderLine.price[0].amount * orderLine.quantity)
          }
          else {
            return acc
          }
        }
        return acc + (orderLine.price[0].amount * orderLine.quantity)
      }, 0)
    },
    serviceChargesOrderLines () {
      const serviceCharges = this.order.orderLines.filter((orderLine) => orderLine.orderLineType === orderLineTypes.SERVICE_CHARGE && orderLine.price[0].amount)
      return serviceCharges
    },
    serviceChargesToShow () {
      const setOfValues = new Set()
      const valuesOfServiceCharges = {}
      for (let serviceCharge of this.serviceChargesOrderLines) {
        const { value, quantity, price } = serviceCharge
        const { amount, currency } = price[0]
        if (setOfValues.has(value)) {
          valuesOfServiceCharges[value].amount += amount * quantity
        }
        else {
          setOfValues.add(value)
          valuesOfServiceCharges[value] = {
            amount: amount * quantity,
            currency,
            value,
          }
        }
      }
      return Object.values(valuesOfServiceCharges)
    },
    totalPrice () {
      if (!this.order) return null
      const total = this.serviceChargesToShow.reduce(
        (acc, serviceCharge) => acc + serviceCharge.amount,
        this.subtotal
      )
      return total
    },
    image () {
      return this.reservation.webappPage.coverImageUrl
    },
    title () {
      return getTextValue(this.reservation.webappPage.pageSpecific.title)
    },
    date () {
      const date = this.reservation.reservation.acceptedDate || this.reservation.reservation.requestedDate
      return displayFormatDate(date, 'sm', true)
    },
    time () {
      const time = this.reservation.reservation.acceptedTime || this.reservation.reservation.requestedTime
      return moment.utc(time).format(getTimeFormat())
    },
    attendees () {
      const numeralAttendees = this.reservation.numeralAttendees.reduce((total, attendee) => {
        return total + attendee.quantity
      }, 0)
      const nominalAttendees = this.reservation.nominalAttendees.length
      return numeralAttendees || nominalAttendees
    },
    room () {
      const room = this.isRoomService && this.reservation.room ? this.reservation.room.name : null
      return room
    },
    status () {
      return this.reservation.reservation.status
    },
    isCompleted () {
      return [activityReservationStatus.ATTENDED, activityReservationStatus.CANCELLED, activityReservationStatus.REJECTED, activityReservationStatus.ABSENT, activityReservationStatus.CANCELLED_BY_ACTIVITY_MODIFICATION].includes(this.status)
    },
    isRoomService () {
      return this.reservation.reservation.type === reservationTypes.ROOM_SERVICE
    },
    isUpcoming () {
      const lessThan24H = moment.duration(moment(this.reservation.reservation.acceptedDate || this.reservation.reservation.requestedDate).diff(moment())).asHours() <= 24
      return lessThan24H && this.reservation.reservation.status === activityReservationStatus.ACCEPTED && !this.isRoomService
    },
    statusText () {
      if (this.isUpcoming) {
        return this.$t('reservations.short_status.UPCOMING')
      }
      if (this.isRoomService && this.$te('reservations.order_short_status.' + this.status)) {
        return this.$t('reservations.order_short_status.' + this.status)
      } else if (this.$te('reservations.short_status.' + this.status)) {
        return this.$t('reservations.short_status.' + this.status)
      } else if (this.isRoomService && this.$te('reservations.order_status.' + this.status)) {
        return this.$t('reservations.order_status.' + this.status)
      } else {
        return this.$t('reservations.status.' + this.status)
      }
    },
    ticketFileId () {
      return this.reservation.reservation.ticketFileId
    },
    reservationMethod () {
      if (this.isRoomService) {
        return this.$t('pages.reservation_type.room_service')
      } else {
        return this.$t('pages.reservation_type.in_venue')
      }
    },
  },
  methods: {
    goTo () {
      this.$emit('openDetails')
    },
    openTicket () {
      activityReservationsService.getTicket(this.reservation.reservation.id, this.ticketFileId)
        .then(route => {
          window.open(route)
        })
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>

.price-chip {
  border-radius: 5px;
  background: #FFF;
  display: flex;
  padding: 6px;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  &.two-currencies {
    padding: 4px 6px !important;
  }
  span {
    line-height: 12px;
  }
}
.v-image {
  .float-right {
    position: absolute;
    right: 15px;
  }
  .float-top {
    top: 15px;
  }
  .float-bottom {
    bottom: 15px;
  }
  .float-left {
    position: absolute;
    left: 15px;
  }
}
</style>
